import { API_CONFIG, apiCall } from './api';
import { authAPI } from './auth-api';

export const settingsAPI = {
  // Fetch all general configs
  getAllGeneralConfigs: async () => {
    try {
      const response = await apiCall(API_CONFIG.ENDPOINTS.GENERAL_CONFIG_LIST, {
        method: 'GET',
      });
      return response;
    } catch (error) {
      console.error('Error fetching general configs:', error);
      throw error;
    }
  },

  // Update express delivery settings
  updateExpressDelivery: async (payload) => {
    try {
      const response = await apiCall(API_CONFIG.ENDPOINTS.GENERAL_CONFIG_EXPRESS, {
        method: 'PUT',
        body: JSON.stringify(payload),
      });
      return response;
    } catch (error) {
      console.error('Error updating express delivery:', error);
      throw error;
    }
  },

  // Update chat support settings
  updateChatConfig: async (payload) => {
    try {
      const response = await apiCall(API_CONFIG.ENDPOINTS.GENERAL_CONFIG_CHAT, {
        method: 'PUT',
        body: JSON.stringify(payload),
      });
      return response;
    } catch (error) {
      console.error('Error updating chat settings:', error);
      throw error;
    }
  },

  // Update general settings
  updateGeneralSettings: async (payload) => {
    try {
      const response = await apiCall(API_CONFIG.ENDPOINTS.GENERAL_CONFIG_UPDATE, {
        method: 'PUT',
        body: JSON.stringify(payload),
      });
      return response;
    } catch (error) {
      console.error('Error updating general settings:', error);
      throw error;
    }
  },

  updateTimeSlots: async (payload) => {
    try {
      const response = await apiCall(API_CONFIG.ENDPOINTS.CONFIG_TIME_SLOT_UPDATE, {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      return response;
    } catch (error) {
      console.error('Error updating time slots:', error);
      throw error;
    }
  },

  updateSpinWheelConfig: async ({
    weeklyWinnersCount,
    giftName,
    nextWinnerSelectionDate,
    giftPhotoFile,
    isGiftEnabled,
    removeGiftPhoto,
  }) => {
    try {
      const formData = new FormData();
      formData.append('weeklyWinnersCount', weeklyWinnersCount);
      formData.append('giftName', giftName);
      formData.append('isGiftEnabled', isGiftEnabled ? 'true' : 'false');
      if (nextWinnerSelectionDate) {
        formData.append('nextWinnerSelectionDate', nextWinnerSelectionDate);
      }
      if (removeGiftPhoto) {
        formData.append('removeGiftPhoto', 'true');
      }
      if (giftPhotoFile instanceof File) {
        formData.append('giftPhoto', giftPhotoFile);
      }
      const response = await apiCall(API_CONFIG.ENDPOINTS.GENERAL_CONFIG_SPIN_WHEEL, {
        method: 'POST',
        body: formData,
      });
      return response;
    } catch (error) {
      console.error('Error updating spin wheel settings:', error);
      throw error;
    }
  },

  // Clear cached homepage content (categories, banners, deal zone, reels)
  clearRedisCache: async () => {
    try {
      const response = await apiCall(API_CONFIG.ENDPOINTS.GENERAL_CONFIG_CLEAR_CACHE, {
        method: 'DELETE',
      });
      return response;
    } catch (error) {
      console.error('Error clearing redis cache:', error);
      throw error;
    }
  },

  uploadAdminApp: async ({ version, apkFile, onProgress }) => {
    try {
      const formData = new FormData();
      formData.append('adminAppVersion', version);
      if (apkFile instanceof File) {
        formData.append('apk', apkFile);
      }

      // XHR so we can report real multipart upload progress for large APKs.
      const url = `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.GENERAL_CONFIG_ADMIN_APP}`;
      const token = authAPI.getToken();

      return await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('POST', url);

        if (token) {
          xhr.setRequestHeader('Authorization', `Bearer ${token}`);
        }

        xhr.upload.onprogress = (event) => {
          if (!onProgress || !event.lengthComputable || event.total <= 0) return;
          const percent = Math.min(100, Math.round((event.loaded / event.total) * 100));
          onProgress({
            loaded: event.loaded,
            total: event.total,
            percent,
          });
        };

        xhr.onload = () => {
          if (xhr.status === 401) {
            authAPI.handleSessionExpired();
            resolve({
              success: false,
              error: 'Unauthorized',
              message: 'Session expired',
            });
            return;
          }

          let data = {};
          try {
            data = xhr.responseText ? JSON.parse(xhr.responseText) : {};
          } catch {
            data = {
              success: false,
              msg: xhr.responseText || `Request failed (${xhr.status})`,
            };
          }

          if (xhr.status < 200 || xhr.status >= 300) {
            resolve({
              success: false,
              ...data,
              msg: data.msg || data.error || `Request failed (${xhr.status})`,
            });
            return;
          }

          if (typeof onProgress === 'function') {
            onProgress({ loaded: 1, total: 1, percent: 100 });
          }
          resolve(data);
        };

        xhr.onerror = () => reject(new Error('Network error while uploading admin app'));
        xhr.onabort = () => reject(new Error('Upload aborted'));
        xhr.send(formData);
      });
    } catch (error) {
      console.error('Error uploading admin app:', error);
      throw error;
    }
  },

  // Update the cycling search suggestion list (up to 6 items) shown in the app search bar
  updateSearchSuggestions: async (suggestions) => {
    try {
      const response = await apiCall(API_CONFIG.ENDPOINTS.GENERAL_CONFIG_SEARCH_SUGGESTIONS, {
        method: 'PUT',
        body: JSON.stringify({ suggestions }),
      });
      return response;
    } catch (error) {
      console.error('Error updating search suggestions:', error);
      throw error;
    }
  },
};